import React from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { origins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { pricingTableRegion } from '@chegg-wt/core/state/instrumentation/rio/clickstreamSignupStart'
import {
  CHECK,
  PRODUCT_VALUE_MAP,
  PRICING_TABLE_STUDY_PACK,
  PRICING_TABLE_PLUS,
  PRICING_TABLE_FREE,
} from '@chegg-wt/core/constants/pricingTable'
import {
  PricingTableProduct,
  PricingTable as PricingTableType,
  WebRoute,
} from '@chegg-wt/core/types/core'
import { userClick, signupStart } from '@chegg-wt/core/state/instrumentation/actions'
import { AppState } from '@chegg-wt/core/state/types'
import { authModalExpFlagSelector } from '@chegg-wt/core/state/createCitation/selectors'
import { isGuestSelector } from '@chegg-wt/core/state/user/selectors'
import { Config } from '@chegg-wt/core/config'
import { CITE_THIS_FOR_ME } from '@chegg-wt/core/constants/site'
import { startAuthFlow } from '@chegg-wt/core/state/user/actions'
import { LvpMarginWrapper } from '../Layouts/LargeVideoPlayerLayout'
import { AuthModalOrigin } from '../Modals/AuthModal/types'
import { useRoutesContext } from '../RoutesContext'
import * as Styled from './styled'
import { useSignUpLink } from './useSignUpLink'
import { PRICING_TABLE, PRICING_TABLE_HEADER } from './testIds'
import { DiscountSaleBadge } from './DiscountSaleBadge'

interface BaseProps {
  currentProductId?: string
  onSignUpClick?: (
    type: string,
    studyPackUrl: string,
    showAuthModal: boolean,
    toUpgrade: () => WebRoute
  ) => () => void
}
interface PricingTableProps extends BaseProps {
  tableData: PricingTableType
  discountPercentage?: number
  authModalExpFlag?: boolean
  isGuestUser?: boolean
}

interface SignUpButtonProps extends BaseProps {
  product: PricingTableProduct
  studyPackUrl: string
  showAuthModal: boolean
  guestUser: boolean
}

export const ProductFeature: React.FC<{
  product: PricingTableProduct
  feature: string
}> = ({ product, feature }) => {
  const featureName = product.features[feature] || ''
  const isCheck = featureName === CHECK

  return (
    <Styled.Feature data-test={`${product.id}-${feature}`}>
      {isCheck ? (
        <Styled.Check
          name="checkmark"
          title={`checkmark-${product.id}-${feature}`}
          aria-label="included"
        />
      ) : (
        featureName
      )}
    </Styled.Feature>
  )
}

const SignUpButton: React.FC<SignUpButtonProps> = ({
  product,
  studyPackUrl,
  currentProductId,
  onSignUpClick,
  showAuthModal,
  guestUser,
}) => {
  const url = useSignUpLink(product.id, studyPackUrl)
  const { toUpgrade } = useRoutesContext()

  const showAuthModalFlag =
    showAuthModal &&
    guestUser &&
    (product.id === PRICING_TABLE_FREE || product.id === PRICING_TABLE_PLUS)
  let buttonText = currentProductId === product.id ? 'Current' : 'Sign up'

  const showTryItForFree =
    Config.flankerSitesBannerCTAUpdate &&
    Config.tokenSiteCode !== CITE_THIS_FOR_ME &&
    url === '/upgrade' &&
    buttonText === 'Sign up'
  if (showTryItForFree) {
    buttonText = 'Try for free'
  }
  const button = product.recommended ? (
    <Styled.LinkRecommended
      onClick={onSignUpClick(product.id, studyPackUrl, showAuthModalFlag, toUpgrade)}
      data-test={`${product.id}-btn`}
      href={showAuthModalFlag ? undefined : url}
    >
      {buttonText}
    </Styled.LinkRecommended>
  ) : (
    <Styled.Link
      onClick={onSignUpClick(product.id, studyPackUrl, showAuthModalFlag, toUpgrade)}
      data-test={`${product.id}-btn`}
      href={showAuthModalFlag ? undefined : url}
    >
      {buttonText}
    </Styled.Link>
  )
  return (
    <Styled.Action role="cell" recommended={product.recommended} key={product.id}>
      {button}
    </Styled.Action>
  )
}

const ProductTitle: React.FC<{
  productId: string
  productName: string
}> = ({ productId, productName }) => {
  switch (productId) {
    case PRICING_TABLE_FREE:
      return <Styled.ProductTitleFree>{productName}</Styled.ProductTitleFree>
    case PRICING_TABLE_PLUS:
      return <Styled.ProductTitlePlus>{productName}</Styled.ProductTitlePlus>
    default:
      return <span>{productName}</span>
  }
}

export const PricingTable: React.FC<PricingTableProps> = ({
  tableData,
  currentProductId,
  discountPercentage,
  onSignUpClick,
  authModalExpFlag,
  isGuestUser,
}) => {
  const hasDiscountPercentage = discountPercentage > 0
  return (
    <Styled.Container data-test={PRICING_TABLE}>
      <LvpMarginWrapper>
        <Styled.Table
          role="table"
          aria-label="Membership"
          aria-describedby="membership_table_desc"
        >
          <Styled.Title data-test={PRICING_TABLE_HEADER} id="membership_table_desc">
            {tableData.title}
          </Styled.Title>
          <Styled.Products role="rowgroup">
            <Styled.ProductNames role="row">
              <Styled.Spacer role="columnheader" aria-label="features" />
              {tableData.products.map((p) => (
                <Styled.ProductName
                  role="columnheader"
                  key={p.name}
                  data-test={p.id}
                  hasDiscountPercentage={hasDiscountPercentage}
                >
                  {hasDiscountPercentage && p.id === PRICING_TABLE_PLUS && (
                    <Styled.DiscountBadgeWrapper>
                      <DiscountSaleBadge discountPercentage={discountPercentage} />
                    </Styled.DiscountBadgeWrapper>
                  )}
                  {hasDiscountPercentage ? (
                    <ProductTitle productId={p.id} productName={p.name} />
                  ) : (
                    p.name
                  )}
                </Styled.ProductName>
              ))}
            </Styled.ProductNames>
          </Styled.Products>
          <Styled.Features role="rowgroup">
            {tableData.features.map((feature) => (
              <Styled.FeatureRow role="row" key={feature.id}>
                <Styled.FeatureWrapper role="rowheader">
                  <Styled.Feature data-test={feature.id}>{feature.name}</Styled.Feature>
                </Styled.FeatureWrapper>
                {tableData.products.map((product) => (
                  <Styled.FeatureWrapper
                    role="cell"
                    recommended={product.recommended}
                    key={product.id}
                  >
                    <ProductFeature product={product} feature={feature.id} />
                  </Styled.FeatureWrapper>
                ))}
              </Styled.FeatureRow>
            ))}
          </Styled.Features>
          <Styled.PricesAndActions role="rowgroup">
            <Styled.Prices role="row">
              <Styled.Spacer role="cell" />
              {tableData.products.map((product) => (
                <Styled.Price
                  role="cell"
                  recommended={product.recommended}
                  key={product.id}
                >
                  {product.recommended && discountPercentage > 0 ? (
                    <>
                      <div data-test={`${product.id}-offer-price`}>{product.price}</div>
                      <Styled.StrikedPrice>${product.basePrice}</Styled.StrikedPrice>
                    </>
                  ) : (
                    <div data-test={`${product.id}-price`}>{product.price}</div>
                  )}
                </Styled.Price>
              ))}
            </Styled.Prices>
            <Styled.Actions role="row">
              <Styled.Spacer role="cell" />
              {tableData.products.map((product) => (
                <SignUpButton
                  product={product}
                  onSignUpClick={onSignUpClick}
                  currentProductId={currentProductId}
                  studyPackUrl={tableData.studyPackUrl}
                  key={product.id}
                  showAuthModal={authModalExpFlag}
                  guestUser={isGuestUser}
                />
              ))}
            </Styled.Actions>
          </Styled.PricesAndActions>
          {tableData.termsAndConditionsUrl && (
            <Styled.Footer>
              <Styled.TermsAndConditions
                href={tableData.termsAndConditionsUrl}
                target="_blank"
              >
                *See Terms and Conditions
              </Styled.TermsAndConditions>
            </Styled.Footer>
          )}
        </Styled.Table>
      </LvpMarginWrapper>
    </Styled.Container>
  )
}

export const mapDispatchToProps = (dispatch: Dispatch) => ({
  onSignUpClick: (
    productId: string,
    studyPackUrl: string,
    showAuthModal: boolean,
    toUpgrade: () => WebRoute
  ) => (event: React.MouseEvent) => {
    dispatch(signupStart({ region: pricingTableRegion }))
    dispatch(
      userClick({ origin: origins.signUpClick, value: PRODUCT_VALUE_MAP[productId] })
    )
    if (productId === PRICING_TABLE_STUDY_PACK) {
      event.preventDefault()
      window.open(studyPackUrl, '_blank')
    } else if (showAuthModal) {
      dispatch(
        startAuthFlow({
          defaultFlow: 'signup',
          origin: AuthModalOrigin.PRICE_TABLE,
          redirectTo: productId === PRICING_TABLE_PLUS ? toUpgrade() : undefined,
        })
      )
    }
  },
})

const mapStateToProps = (state: AppState) => ({
  authModalExpFlag: authModalExpFlagSelector(state),
  isGuestUser: isGuestSelector(state),
})

export default connect(mapStateToProps, mapDispatchToProps)(PricingTable)
